import { ChevronLeftIcon, ChevronRightIcon, ClockIcon } from "@heroicons/react/20/solid";
import { FilmIcon } from "@heroicons/react/24/outline";
import { getVideoSceneFrameUrl } from "@/api/renderer/api";
import { VideoInsightScene } from "@/api/studio/insights/types/attention.type";
import { Progress } from "@/components/ui/progress";
import { VideoMetricRow } from "@/features/reports/components/video-metric-row";
import { asPlaybackTime } from "@/utils/format";
import { cn } from "@/utils/classes";

/* Prop Types */
interface VideoSceneDetailProps {
  projectId: string;
  studyId: string;
  videoKey: string;
  scenes: VideoInsightScene[];
  selectedSceneKey: string | null;
  onSelectScene: (sceneKey: string) => void;
}

/**
 * VideoSceneDetail component
 *
 * Shows the currently selected scene's thumbnail, time range, duration, description, and
 * Attention/Hook/Visual ease/Hold breakdown, with prev/next navigation between scenes. Renders a
 * placeholder until a scene is selected.
 */
export const VideoSceneDetail = ({ projectId, studyId, videoKey, scenes, selectedSceneKey, onSelectScene }: VideoSceneDetailProps) => {
  const selectedIndex = scenes.findIndex((scene) => scene.scene === selectedSceneKey);
  const selectedScene = selectedIndex === -1 ? undefined : scenes[selectedIndex];

  if (!selectedScene) {
    return (
      <div className="flex items-center justify-center gap-2 bg-[#242c35] rounded-2xl p-4 h-[200px]">
        <FilmIcon className="size-5 text-[#9ca3af]" />
        <p className="text-[#9ca3af] font-medium">Select a scene to reveal metrics</p>
      </div>
    );
  }

  const { scores } = selectedScene;

  /* Render */
  return (
    <div className="flex flex-col gap-3 bg-[#242c35] rounded-2xl p-4 min-w-0">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <p className="text-white text-sm font-semibold">Scene {selectedIndex + 1}</p>
        <div className="flex-1" />
        <span className="text-[#9ca3af] text-xs font-semibold">
          {selectedIndex + 1} of {scenes.length}
        </span>
        <button
          type="button"
          onClick={() => onSelectScene(scenes[selectedIndex - 1].scene)}
          disabled={selectedIndex === 0}
          aria-label="Previous scene"
          className={cn("flex items-center justify-center size-8 rounded-lg", selectedIndex === 0 ? "opacity-30" : "cursor-pointer hover:bg-[#323c49]")}
        >
          <ChevronLeftIcon className="size-4 text-white" />
        </button>
        <button
          type="button"
          onClick={() => onSelectScene(scenes[selectedIndex + 1].scene)}
          disabled={selectedIndex === scenes.length - 1}
          aria-label="Next scene"
          className={cn("flex items-center justify-center size-8 rounded-lg", selectedIndex === scenes.length - 1 ? "opacity-30" : "cursor-pointer hover:bg-[#323c49]")}
        >
          <ChevronRightIcon className="size-4 text-white" />
        </button>
      </div>

      {/* Thumbnail, time range, duration, description, and scores */}
      <div className="flex gap-6 items-start">
        <div className="w-[280px] h-full shrink-0 rounded-lg overflow-hidden relative bg-[#323c49]">
          {selectedScene.frames[0] && (
            <img
              src={getVideoSceneFrameUrl({ projectId, studyId, assetKey: videoKey, frameName: selectedScene.frames[0] })}
              alt={`Scene ${selectedIndex + 1}`}
              className="absolute inset-0 size-full object-cover"
            />
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <FilmIcon className="size-4 text-white" />
              <span className="text-white text-sm font-semibold">
                {asPlaybackTime(selectedScene.startTime)} - {asPlaybackTime(selectedScene.endTime)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <ClockIcon className="size-4 text-white" />
              <span className="text-white text-sm font-semibold">{selectedScene.duration.toFixed(2)}s</span>
            </div>
          </div>
          <p className="text-white text-sm">{selectedScene.description}</p>
        </div>

        <div className="w-[380px] shrink-0 flex flex-col rounded-lg overflow-hidden">
          <div className="flex items-center justify-between gap-3 bg-[#323c49] px-4 py-3">
            <span className="text-white text-sm font-semibold">Attention</span>
            <div className="flex items-center gap-3">
              <Progress value={typeof scores.attention === "number" ? scores.attention : 0} className="w-[93px] h-2 bg-base-100" />
              <span className="text-white text-sm font-semibold w-[38px] text-right">{typeof scores.attention === "number" ? `${Math.round(scores.attention)}%` : "Later"}</span>
            </div>
          </div>
          <div className="bg-[#323c49] px-4 py-3 text-white [&_svg]:text-white/50">
            <VideoMetricRow label="Hook" score={scores.hook} nullLabel="Earlier" />
          </div>
          <div className="bg-[#323c49] px-4 py-3 text-white [&_svg]:text-white/50">
            <VideoMetricRow label="Visual ease" score={scores.visualEase} />
          </div>
          <div className="bg-[#323c49] px-4 py-3 text-white [&_svg]:text-white/50">
            <VideoMetricRow label="Hold" score={scores.hold} />
          </div>
        </div>
      </div>
    </div>
  );
};
